export type SessionInfo = {
    readonly userId: string,
    readonly authToken: string,
    readonly refreshToken: string
}

export type TokenInfo = {
    readonly expiresAt: Date,
    readonly refreshAt: number
}


export type SessionHandlers = {
    readonly tokenRefresher: (refreshToken: string, authToken: string) => Promise<{
        authToken: string,
        tokenInfo: TokenInfo,
        refreshToken: string,
    } | null>,
    readonly tokenValidator: (token: string) => Promise<boolean>,
    readonly needSaveSession: (session: Session, sessionInfo: SessionInfo) => void,
    readonly sessionClosed: (session: Session) => void,
}


class SessionData {
    public info: SessionInfo;
    public tokenInfo: TokenInfo;

    constructor(
        info: SessionInfo,
        tokenInfo: TokenInfo
    ) {
        this.info = info;
        this.tokenInfo = tokenInfo;
    }

    public needRefreshToken(): boolean {
        return this.tokenInfo.refreshAt < Date.now();
    }


    public withUpdatedTokenInfo(authToken: string, tokenInfo: TokenInfo, refreshToken: string): SessionData {
        return new SessionData(
            {
                userId: this.info.userId,
                authToken: authToken,
                refreshToken: refreshToken,
            },
            {
                expiresAt: tokenInfo.expiresAt,
                refreshAt: tokenInfo.refreshAt,
            }
        );
    }
}

export class Session {
    private _sessionData: SessionData | null; // null - if the session is closed
    private readonly _handlers: SessionHandlers;
    private _refreshTokenLock: boolean = false;
    private _pendingRefreshWaiters: (() => void)[] = [];


    private constructor(
        sessionData: SessionData,
        sessionHandlers: SessionHandlers
    ) {
        this._sessionData = sessionData;
        this._handlers = sessionHandlers;
    }


    public static create(
        sessionInfo: SessionInfo,
        tokenInfo: TokenInfo,
        sessionHandlers: SessionHandlers
    ): Session {
        const sessionData = new SessionData(
            {
                userId: sessionInfo.userId,
                authToken: sessionInfo.authToken,
                refreshToken: sessionInfo.refreshToken,
            },
            {
                expiresAt: tokenInfo.expiresAt,
                refreshAt: tokenInfo.refreshAt,
            }
        );

        return new Session(
            sessionData,
            sessionHandlers
        );
    }


    public async getSessionInfo(): Promise<SessionInfo | null> {
        const sessionData = await this.getSessionData();
        return sessionData?.info ?? null;
    }


    public async getAuthToken(): Promise<string | null> {
        const sessionData = await this.getSessionData();
        return sessionData?.info.authToken ?? null;
    }


    public async getUserId(): Promise<string | null> {
        const sessionData = await this.getSessionData();
        return sessionData?.info.userId ?? null;
    }


    public async validate(): Promise<boolean> {
        const sessionData = await this.getSessionData();
        if (!sessionData) return false;

        try {
            const validatorResult = await this._handlers.tokenValidator(sessionData.info.authToken);
            if (!validatorResult) {
                this.closeSession();
            }
            return await this.getSessionData() !== null;
        } catch (error) {
            console.error('Error in validate:', error);
            this.closeSession();
            return false;
        }
    }


    private async getSessionData(): Promise<SessionData | null> {
        // wait for pending refresh promises
        while (this._refreshTokenLock) {
            await new Promise<void>(resolve => {
                this._pendingRefreshWaiters.push(resolve)
            });
        }

        if (this._sessionData?.needRefreshToken()) {
            this._refreshTokenLock = true;
            try {
                await this._refreshToken();
            } finally {
                this._refreshTokenLock = false;
                const waiters = this._pendingRefreshWaiters;
                this._pendingRefreshWaiters = [];
                waiters.forEach(r => r());
            }
        }

        return this._sessionData;
    }


    private async _refreshToken(): Promise<void> {
        if (!this._sessionData) return;
        try {
            const refreshInfo = await this._doRefreshToken(this._sessionData.info);
            if (refreshInfo) {
                if (this._sessionData) {
                    this._sessionData = this._sessionData.withUpdatedTokenInfo(
                        refreshInfo.authToken,
                        refreshInfo.tokenInfo,
                        refreshInfo.refreshToken
                    );
                }
                this._handleUpdatedSession();
            } else {
                this.closeSession();
            }
        } catch (error) {
            console.error('Error in refreshToken:', error);
            this.closeSession();
        }
    }


    private async _doRefreshToken(sessionInfo: SessionInfo): Promise<{
        authToken: string,
        tokenInfo: TokenInfo,
        refreshToken: string
    } | null> {
        try {
            const refreshInfo = await this._handlers.tokenRefresher(
                sessionInfo.refreshToken,
                sessionInfo.authToken
            );
            if (!refreshInfo) {
                console.info('Failed to refresh token');
                return null; // cannot refresh a token
            }
            return {
                authToken: refreshInfo.authToken,
                refreshToken: refreshInfo.refreshToken,
                tokenInfo: {
                    expiresAt: refreshInfo.tokenInfo.expiresAt,
                    refreshAt: refreshInfo.tokenInfo.refreshAt,
                }
            };
        } catch (error) {
            console.error('Failed to refresh token:', error);
            return null;
        }
    }


    private closeSession(): void {
        if (!this._sessionData) return; // already closed

        this._sessionData = null;

        this._refreshTokenLock = false;
        const waiters = this._pendingRefreshWaiters;
        this._pendingRefreshWaiters = [];
        waiters.forEach(r => r());

        try {
            this._handlers.sessionClosed(this);
        } catch (error) {
            console.error('Error in sessionClosed handler:', error);
        }
    }


    private _handleUpdatedSession(): void {
        if (!this._sessionData) return; // the session already closed
        try {
            this._handlers.needSaveSession(this, this._sessionData.info);
        } catch (error) {
            console.error('Error in needSaveSession handler:', error);
        }
        return;
    }
}